import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import Chrome from "@zendesk/knowledge-chrome/lib/Chrome";
import { ContentView, Sidebar } from "@zendesk/guide-chrome";
import {
  getCurrentBrandId,
  hasFeature,
  getHelpCenter,
} from "@zendesk/knowledge-context";
import t from "@zendesk/client-i18n-tools";
import { Pagination } from "@zendeskgarden/react-pagination";
import { Button } from "@zendeskgarden/react-buttons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import createBrandedUrl from "@zendesk/knowledge-utils/lib/createBrandedUrl";
import * as actions from "../../actions/categories";
import ErrorBoundary from "../ErrorBoundary";
import CategoryRow from "../CategoryRow";
import Empty from "../Empty";
import LoadingOverlay from "../LoadingOverlay";
import Notifications from "../Notifications";
import styles from "./index.css";

export class CategoryList extends Component {
  constructor(props) {
    super(props);

    this.onPageSelected = this.onPageSelected.bind(this);
    this.onCategoryClicked = this.onCategoryClicked.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onMoveToTop = this.onMoveToTop.bind(this);
  }

  componentWillMount() {
    const { dispatch, page } = this.props;
    dispatch(actions.fetchCategories({ page }));
  }

  openNewCategoryPage() {
    const hc = getHelpCenter();
    window.open(
      createBrandedUrl(hc.route.subdomain, "/hc/admin/categories/new"),
      "_blank"
    );
  }

  onPageSelected(page) {
    const { dispatch } = this.props;

    dispatch(actions.fetchCategories({ page }));

    dispatch(
      push({
        pathname: `/knowledge/arrange/categories/page/${page}`,
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onCategoryClicked(categoryId) {
    const { dispatch } = this.props;

    dispatch(
      push({
        pathname: `/knowledge/arrange/categories/${categoryId}`,
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onMoveToTop(category, sourceIndex) {
    const { dispatch } = this.props;

    dispatch(actions.moveCategoryToTop(category, sourceIndex));
  }

  onDragEnd(dragEnd) {
    const { source, destination, type, draggableId } = dragEnd;
    const { dispatch } = this.props;

    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    if (type === "category" && destination.droppableId === "categories") {
      dispatch(
        actions.positionCategory(draggableId, source.index, destination.index)
      );
    }
  }

  renderCategories() {
    const {
      categories,
      categorySavingId,
      categorySuccessId,
      isMoving,
    } = this.props;

    return (
      <div>
        {categories.map((category, index) => (
          <Draggable
            key={category.id}
            type="category"
            draggableId={category.id}
            index={index}
            isDragDisabled={isMoving}
          >
            {(providedDraggable, snapshotDraggable) => (
              <div
                className={styles.draggable}
                ref={providedDraggable.innerRef}
                {...providedDraggable.draggableProps}
              >
                <CategoryRow
                  testId="category-item"
                  category={category}
                  onClick={() => this.onCategoryClicked(category.id)}
                  onMoveToTop={() => this.onMoveToTop(category, index)}
                  key={category.id}
                  saving={categorySavingId === category.id}
                  success={categorySuccessId === category.id}
                  dragHandleProps={providedDraggable.dragHandleProps}
                />
              </div>
            )}
          </Draggable>
        ))}
      </div>
    );
  }

  renderContent() {
    const { count, isFetching, pageCount, page } = this.props;

    if (isFetching) {
      return null;
    }

    if (count === 0) {
      return (
        <Empty>
          <Empty.Title>{t("knowledge-arrange.categories.empty")}</Empty.Title>
          <Empty.Description>
            {t("knowledge-arrange.categories.emptyDescription")}
          </Empty.Description>
          <Empty.Actions>
            <Button
              primary
              size="small"
              data-test-id="empty-add-category"
              onClick={this.openNewCategoryPage}
            >
              {t("knowledge-arrange.categories.addCategory")}
            </Button>
          </Empty.Actions>
        </Empty>
      );
    }

    const pagination = pageCount > 1 && (
      <Pagination
        totalPages={pageCount}
        currentPage={page}
        onChange={this.onPageSelected}
      />
    );

    return (
      <div>
        <h3 className={styles.listHeader}>
          {t("knowledge-arrange.categories.pageTitle", { count })}
        </h3>

        <Droppable droppableId="categories" type="category">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {this.renderCategories()}
            </div>
          )}
        </Droppable>

        {pagination}
      </div>
    );
  }

  render() {
    const { hasCommunity } = this.props;
    const hc = getHelpCenter();

    return (
      <Chrome
        brandId={getCurrentBrandId()}
        dir={t.dir}
        topNav={
          <Chrome.Breadcrumbs>
            <Chrome.Breadcrumbs.Item>
              <a href={`/knowledge/arrange?brand_id=${getCurrentBrandId()}`}>
                {t("knowledge-arrange.breadcrumbs.arrangeContent")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>
              {t("knowledge-arrange.name")}
            </Chrome.Breadcrumbs.Item>
          </Chrome.Breadcrumbs>
        }
      >
        <Sidebar>
          <Sidebar.Item active testId="sidebar-user-segments-link">
            {t("knowledge-arrange.name")}
          </Sidebar.Item>

          {hasCommunity && (
            <Sidebar.LinkItem
              href={createBrandedUrl(
                hc.route.subdomain,
                "/hc/admin/arrange_contents#community"
              )}
              testId="sidebar-arrange-topics-link"
            >
              {t("knowledge-arrange.sidebar.arrangeTopics")}
            </Sidebar.LinkItem>
          )}

          <Sidebar.LinkItem
            href={createBrandedUrl(
              hc.route.subdomain,
              "/hc/admin/import_articles"
            )}
            testId="sidebar-import-articles-link"
          >
            {t("knowledge-arrange.sidebar.importArticles")}
          </Sidebar.LinkItem>
        </Sidebar>

        <ContentView>
          <ErrorBoundary>
            <Notifications dir={t.dir} />
            <div className={styles.appHeader}>
              <h1 className={styles.title}>{t("knowledge-arrange.name")}</h1>
            </div>

            <DragDropContext onDragEnd={this.onDragEnd}>
              {this.renderContent()}
            </DragDropContext>

            <LoadingOverlay />
          </ErrorBoundary>
        </ContentView>
      </Chrome>
    );
  }
}

CategoryList.propTypes = {
  dispatch: PropTypes.func,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  isFetching: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  categorySavingId: PropTypes.number,
  categorySuccessId: PropTypes.number,
  isMoving: PropTypes.bool,
  hasCommunity: PropTypes.bool,
};

const mapStateToProps = ({ categories }, { match }) => {
  const page = parseInt(match.params.page, 10);

  return {
    categories: categories.list,
    isFetching: categories.isFetching,
    pageCount: categories.pageCount,
    count: categories.count,
    page: isNaN(page) ? 1 : page,
    categorySavingId: categories.savingId,
    categorySuccessId: categories.successId,
    isMoving: categories.isMoving,
    hasCommunity: hasFeature("community") && getHelpCenter().community,
  };
};

export default connect(mapStateToProps)(CategoryList);



// WEBPACK FOOTER //
// ./src/components/CategoryList/index.js